/**
 * ClusterNeuralMap — neurol-ux referansının birebir mantığı
 *
 *  Düzen: RADYAL DENDROGRAM (radial tidy tree)
 *   • Merkez = domain (anasayfa)
 *   • 1. halka = dil grupları (hexagon)  ← dil bariyeri
 *   • 2. halka = ana cluster'lar (daire)
 *   • 3+ halka = alt cluster'lar
 *   • yapraklar = sayfalar (dikdörtgen, pillar ★ ile belirgin)
 *
 *  Çakışma garantisi:
 *   Her yaprak (sayfa) açısal bir dilim alır → dış halka yarıçapı
 *   yaprak sayısına göre büyür: R = L × spacing / 2π.
 *   Bu sayede 16 sayfa da olsa 2000 sayfa da olsa hiçbir node çakışmaz,
 *   küçük ağaçlar merkeze yakın kalır (fitView otomatik zoom yapar).
 *
 *  Drag-drop: bir sayfayı/cluster'ı başka cluster'ın üstüne bırakınca
 *   backend'e taşıma isteği gider (onMovePage / onMoveCluster).
 */

import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { Hand, MousePointer2, Link2, Scissors, Maximize2, Layers, Trash2, RotateCcw, X, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { useGetPage } from "@workspace/api-client-react";
import {
  ReactFlow, ReactFlowProvider,
  Background, BackgroundVariant, MiniMap,
  Handle, Position,
  useNodesState, useEdgesState, useReactFlow,
  type Node, type Edge, type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { BoardCluster } from "./ClusterBoard";

// ─── Renkler ─────────────────────────────────────────────────────────────────
const N = {
  bg:"#fafaf9", surface:"#ffffff", elevated:"#f4f4f2",
  border:"#e7e7e4", borderS:"#e8edf3",
  text1:"#1d1d1b", text2:"#70706c", textM:"#70706c",
  blue:"#ec6461", purple:"#e83e3c", green:"#12b76a",
  cyan:"#e83e3c", amber:"#f79009", pink:"#f18e8b", red:"#f04438",
} as const;

// TEK PALET: tüm diller brand (#e83e3c) tonlarından — çok renkli kombinasyon yok
const LANG_COLOR: Record<string,string> = {
  TR:"#e83e3c", EN:"#ec6461", AR:"#b12220", DE:"#f18e8b",
  FR:"#d42b29", RU:"#911d1c", ES:"#f7b6b4", IT:"#400b0a",
  ZH:"#771e1d", JA:"#e83e3c", KO:"#ec6461",
};
const LANG_FLAG: Record<string,string> = {
  EN:"🇬🇧", TR:"🇹🇷", AR:"🇸🇦", DE:"🇩🇪",
  FR:"🇫🇷", RU:"🇷🇺", ES:"🇪🇸", IT:"🇮🇹",
  ZH:"🇨🇳", JA:"🇯🇵", KO:"🇰🇷",
};
const HEX = "polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)";

function getLang(name:string){ return name.match(/^\[([A-Z]{2})\] /)?.[1]??null; }
function strip(name:string)  { return name.replace(/^\[[A-Z]{2}\] /,""); }

// ─── Akıllı renk sistemi ──────────────────────────────────────────────────────
// Dil = ana renk ailesi (hue). Derinlik = ton açılması. Alt-cluster = hafif hue
// kayması. Pillar = parlak + canlı. Bilinmeyen diller golden-angle ile dağıtılır
// → 1 dilde de 10 dilde de mantıklı, çakışmayan renkler.
const LANG_HUE: Record<string,number> = {
  EN:1, TR:-14, AR:14, DE:-22, FR:20, RU:-6,
  ES:7, IT:-18, ZH:16, JA:-10, KO:11,
};
// TEK PALET: tüm hue'lar marka rengi (#e83e3c ≈ hue 1) bandına katlanır (335°..25° sarmalı;
// hslHex negatif dereceyi normalleştirir). Üçgen dalga → komşu ton farkları korunur ama bant
// DIŞINA (mavi/yeşil) taşmaz.
function brandHue(h:number){
  const LO=-25, HI=25, W=HI-LO;
  let x=(((h-LO)%(2*W))+2*W)%(2*W);
  if(x>W) x=2*W-x;
  return LO+x;
}
function langHue(lang:string, idx:number){
  return LANG_HUE[lang] ?? brandHue(Math.round((idx*137.508)%360)); // golden-angle → brand bandı
}
// HSL → 6 haneli hex (alpha eklemeleri `${c}38` çalışsın diye)
function hslHex(h:number,s:number,l:number){
  h=((h%360)+360)%360; s/=100; l/=100;
  const a=s*Math.min(l,1-l);
  const f=(n:number)=>{
    const k=(n+h/30)%12;
    const v=l-a*Math.max(-1,Math.min(k-3,9-k,1));
    return Math.round(255*v).toString(16).padStart(2,"0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
// hue + tür + derinlik + pillar → kenar/yazı/glow rengi (parlak)
function colorFor(hueIn:number, kind:"lang"|"cluster"|"page", depth:number, pillar=false){
  const hue=brandHue(hueIn);
  if(kind==="lang")    return hslHex(hue,74,64);
  if(kind==="cluster") return hslHex(hue,64, 58+Math.min(Math.max(depth-2,0),3)*4); // 58→70 derinleştikçe açılır
  if(pillar)           return hslHex(hue,90,68);  // pillar: canlı & parlak
  return hslHex(hue,55,70);                       // normal sayfa
}
// OPAK, CANLI dolgu rengi (arkası gözükmez) — beyaz/kalın yazıyla net okunur
function bgFor(hueIn:number, kind:"lang"|"cluster"|"page", pillar=false){
  const hue=brandHue(hueIn);
  if(kind==="lang")    return hslHex(hue,58,25);
  if(kind==="cluster") return hslHex(hue,54,24);
  if(pillar)           return hslHex(hue,62,31);
  return hslHex(hue,48,23);
}

// ─── Boyutlar ─────────────────────────────────────────────────────────────────
const SZ = {
  DOMAIN: 210, LANG: 150,
  C1: 80, C2: 62, C3: 48,
  PAGE_W: 180, PAGE_H: 46,
};
const PAGE_LABEL_MAX = 46; // sayfa adı en fazla bu kadar karakter
const LEAF_SPACING = 215;  // iki yaprak arası min yay uzunluğu (px) — kart genişliği kadar
const MIN_RING     = 250;  // halkalar arası min yarıçap (px) — derinlik çakışmasını önler

// Cluster boyutu sayfa sayısına göre: çok sayfalı = büyük (boşluğu değerlendir),
// derine inince küçülür. Büyüklük anlamlı bir sinyal.
function clusterSize(depth:number, pageCount:number){
  const base = depth===2?96 : depth===3?70 : 54;
  const max  = depth===2?200: depth===3?138: 100;
  return Math.round(Math.min(max, base + Math.sqrt(Math.max(pageCount,0))*16));
}
// uzun isimleri "…" ile kes
function ellipsize(s:string, max:number){
  return s.length>max ? s.slice(0,max-1).trimEnd()+"…" : s;
}

// ─── Görünmez handle'lar (çizgiler node merkezine bağlanır) ──────────────────
const HC = { opacity:0, pointerEvents:"none" as const };
function Handles(){
  return <>
    <Handle id="s" type="source" position={Position.Top}    style={HC}/>
    <Handle id="t" type="target" position={Position.Bottom} style={HC}/>
    <Handle id="s2" type="source" position={Position.Right} style={HC}/>
    <Handle id="t2" type="target" position={Position.Left}  style={HC}/>
  </>;
}

// ─── Node bileşenleri ─────────────────────────────────────────────────────────
function DomainNode({data}:NodeProps){
  const d=data as {label:string;clusterCount:number;pageCount:number};
  return (
    <div style={{width:SZ.DOMAIN,height:SZ.DOMAIN,borderRadius:"50%",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      background:"radial-gradient(circle at 32% 22%,#ec6461 0%,#e83e3c 24%,#d42b29 56%,#911d1c 100%)",
      border:"2px solid rgba(255,255,255,.9)",
      boxShadow:"inset 0 -10px 20px rgba(119,30,29,.4),inset 0 7px 12px rgba(255,255,255,.18),0 16px 26px -8px rgba(232,62,60,.45)",
      userSelect:"none",cursor:"default"}}>
      <Handles/>
      <span style={{fontSize:15,fontWeight:800,color:"#ffffff",textShadow:"0 1px 6px rgba(20,20,18,.45)",textAlign:"center",padding:"0 22px",lineHeight:1.3,
        overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{d.label}</span>
      <span style={{fontSize:11,color:"rgba(255,255,255,.92)",textShadow:"0 1px 4px rgba(20,20,18,.5)",marginTop:7}}>{d.clusterCount} cluster · {d.pageCount} sayfa</span>
    </div>
  );
}

function LangNode({data,selected}:NodeProps){
  const d=data as {label:string;flag:string;count:number;accent:string;bg:string;expanded?:boolean;isOrphan?:boolean};
  const c=d.accent; const open=d.expanded!==false;
  return (
    <div title={d.isOrphan?"Atanmamış sayfalar — cluster'a sürükle":open?"Kapatmak için tıkla":"Açmak için tıkla"}
      style={{width:SZ.LANG,height:SZ.LANG,clipPath:HEX,position:"relative",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      background:`radial-gradient(circle at 32% 22%,rgba(255,255,255,.28) 0%,${c} 20%,${d.bg} 52%,${d.bg} 88%,rgba(20,20,18,.4) 100%)`,
      border:`3px solid ${selected||open?"rgba(255,255,255,.95)":"rgba(255,255,255,.6)"}`,
      filter:open?`drop-shadow(0 14px 18px ${c}66)`:`drop-shadow(0 8px 12px ${c}44)`,
      boxShadow:open?`inset 0 -12px 22px rgba(20,20,18,.3),inset 0 8px 14px rgba(255,255,255,.14)`:`inset 0 -8px 15px rgba(20,20,18,.26)`,
      transition:"filter .2s,box-shadow .2s,transform .2s",cursor:"pointer",userSelect:"none"}}>
      <Handles/>
      <span style={{fontSize:28,lineHeight:1}}>{d.flag}</span>
      <span style={{fontSize:d.isOrphan?13:16,fontWeight:800,color:"#ffffff",marginTop:4,textShadow:"0 1px 3px rgba(20,20,18,.85)"}}>{d.label}</span>
      <span style={{fontSize:10,fontWeight:700,color:"#e6eaf2",marginTop:2,textShadow:"0 1px 3px rgba(20,20,18,.8)"}}>
        {d.count} {d.isOrphan?"sayfa":`cluster ${open?"▾":"▸"}`}
      </span>
    </div>
  );
}

function ClusterCircle({data,selected}:NodeProps){
  const d=data as {label:string;pageCount:number;accent:string;bg:string;sz:number;
    dropping?:boolean;hiddenCount?:number;expanded?:boolean;onTrash?:()=>void};
  const c=d.accent; const sz=d.sz;
  const hasHidden=(d.hiddenCount??0)>0;
  const fs = sz>120?13 : sz>85?11 : sz>62?9.5 : 8.5;
  const stop=(e:React.SyntheticEvent)=>e.stopPropagation();
  return (
    <div style={{width:sz,height:sz,borderRadius:"50%",position:"relative",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      background:`radial-gradient(circle at 32% 22%,rgba(255,255,255,.26) 0%,${c} 18%,${d.bg} 50%,${d.bg} 88%,rgba(20,20,18,.4) 100%)`,
      border:`${d.dropping?3.5:selected||d.expanded?2.5:2}px solid ${d.dropping?N.green:"rgba(255,255,255,.9)"}`,
      boxShadow:`inset 0 -${Math.max(6,sz*0.12)}px ${Math.max(12,sz*0.2)}px rgba(20,20,18,.3),inset 0 ${Math.max(4,sz*0.07)}px ${Math.max(8,sz*0.12)}px rgba(255,255,255,.16)`,
      filter:d.dropping?`drop-shadow(0 12px 16px ${N.green}88)`:selected||d.expanded?`drop-shadow(0 14px 18px ${c}70)`:`drop-shadow(0 8px 12px ${c}4d)`,
      transition:"filter .15s,box-shadow .15s,border-color .15s,transform .15s",cursor:"pointer",userSelect:"none"}}>
      <Handles/>
      <span style={{fontSize:fs,fontWeight:800,color:"#ffffff",textAlign:"center",padding:"0 7px",lineHeight:1.25,
        textShadow:"0 1px 3px rgba(20,20,18,.85),0 0 6px rgba(20,20,18,.5)",
        overflow:"hidden",display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical"}}>{d.label}</span>
      {d.pageCount>0&&<span style={{fontSize:Math.max(8,fs-2),fontWeight:700,color:"#ffffff",marginTop:2,textShadow:"0 1px 3px rgba(20,20,18,.8)"}}>{d.pageCount} sayfa</span>}
      {hasHidden&&(
        <span style={{position:"absolute",bottom:-7,right:-7,minWidth:18,height:18,padding:"0 4px",
          borderRadius:9,background:c,color:"#ffffff",fontSize:9,fontWeight:800,
          display:"flex",alignItems:"center",justifyContent:"center",
          boxShadow:`0 2px 6px rgba(20,20,18,.25)`,border:"1.5px solid #ffffff"}}>
          +{d.hiddenCount}
        </span>
      )}
      {d.onTrash&&(
        <button title="Cluster'ı sil (sayfalar atanmamışa)" onClick={(e)=>{stop(e);d.onTrash!();}}
          onMouseDown={stop} onPointerDown={stop}
          style={{position:"absolute",top:-8,left:-8,width:22,height:22,borderRadius:"50%",
            background:"#f4f4f2",border:`1.5px solid ${N.red}`,color:N.red,cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,
            boxShadow:`0 0 8px ${N.red}60`}}>🗑</button>
      )}
    </div>
  );
}

function PageRect({data,selected}:NodeProps){
  const d=data as {label:string;isPillar:boolean;accent:string;bg:string;onPillar?:()=>void;onTrash?:()=>void};
  const c=d.accent; const p=d.isPillar;
  const stop=(e:React.SyntheticEvent)=>{ e.stopPropagation(); };
  return (
    <div style={{minWidth:130,maxWidth:SZ.PAGE_W,borderRadius:9,padding:p?"7px 11px 8px":"7px 11px",position:"relative",
      display:"flex",flexDirection:"column",gap:4,
      background:d.bg,
      border:`${p?2.5:1.8}px solid ${selected?"#fff":c}`,
      boxShadow:selected?`0 0 18px ${c}aa`:p?`0 0 18px ${c}90`:`0 0 10px ${c}66`,
      transition:"box-shadow .15s",cursor:"grab",userSelect:"none"}}>
      <Handles/>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        {p&&(
          <span style={{display:"inline-flex",alignItems:"center",gap:3,
            background:c,color:"#ffffff",fontSize:8,fontWeight:800,letterSpacing:".04em",
            padding:"1.5px 6px",borderRadius:5,boxShadow:`0 0 8px ${c}aa`}}>
            ★ PILLAR PAGE
          </span>
        )}
        {/* pillar yap / kaldır + çöp kutusu */}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
          {d.onPillar&&(
            <button title={p?"Pillar'ı kaldır":"Pillar yap"} onClick={(e)=>{stop(e);d.onPillar!();}}
              onMouseDown={stop} onPointerDown={stop}
              style={{border:"none",background:"transparent",cursor:"pointer",
                fontSize:13,lineHeight:1,padding:0,opacity:p?1:0.55,filter:p?`drop-shadow(0 0 4px ${c})`:"none"}}>
              {p?"⭐":"☆"}
            </button>
          )}
          {d.onTrash&&(
            <button title="Sayfayı atanmamışa al" onClick={(e)=>{stop(e);d.onTrash!();}}
              onMouseDown={stop} onPointerDown={stop}
              style={{border:"none",background:"transparent",cursor:"pointer",fontSize:12,lineHeight:1,padding:0,opacity:0.75}}>
              🗑
            </button>
          )}
        </div>
      </div>
      <span style={{fontSize:p?11.5:10.5,fontWeight:p?800:700,color:"#ffffff",lineHeight:1.3,
        textShadow:"0 1px 3px rgba(20,20,18,.85),0 0 6px rgba(20,20,18,.5)",
        overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",wordBreak:"break-word"}}>
        {d.label}
      </span>
    </div>
  );
}

const nodeTypes={domain:DomainNode,lang:LangNode,cluster:ClusterCircle,page:PageRect};

// ─── Sayfa link özeti paneli ──────────────────────────────────────────────────
function PageLinkSummary({pageId,onClose,glass}:{pageId:number;onClose:()=>void;glass:React.CSSProperties}){
  const {data:page,isLoading}=useGetPage(pageId,{query:{queryKey:["page",pageId]}});
  const p=page as undefined | {
    title?:string|null;url:string;score?:number|null;scoreLabel?:string|null;
    incomingLinkCount?:number;outgoingLinkCount?:number;clusterName?:string|null;isFeatured?:boolean;
  };
  const scoreColor=(p?.score??0)>=71?N.green:(p?.score??0)>=41?N.amber:N.red;
  return (
    <div style={{...glass,position:"absolute",top:14,right:14,zIndex:30,width:290,borderRadius:12,padding:14}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:8}}>
        <span style={{fontSize:9,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:N.textM}}>SAYFA LİNK ÖZETİ</span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {p?.url&&(
            <a href={p.url} target="_blank" rel="noopener noreferrer" title="Sayfaya git"
              style={{textDecoration:"none",fontSize:14,lineHeight:1,cursor:"pointer"}}>🔗</a>
          )}
          <button onClick={onClose} style={{border:"none",background:"transparent",cursor:"pointer",color:N.textM,padding:0,display:"flex"}}><X size={15}/></button>
        </div>
      </div>
      {isLoading||!p ? (
        <p style={{fontSize:12,color:N.text2,margin:0}}>Yükleniyor…</p>
      ):(
        <>
          <p style={{fontSize:13,fontWeight:600,color:N.text1,margin:"0 0 3px",lineHeight:1.3,
            display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
            {p.title||p.url}
          </p>
          <p style={{fontSize:10,color:N.textM,margin:"0 0 10px",wordBreak:"break-all",
            display:"-webkit-box",WebkitLineClamp:1,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{p.url}</p>

          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <div style={{flex:1,background:N.elevated,borderRadius:8,padding:"8px 10px",display:"flex",alignItems:"center",gap:7}}>
              <ArrowDownLeft size={16} color={N.green}/>
              <div><div style={{fontSize:16,fontWeight:700,color:N.text1,lineHeight:1}}>{p.incomingLinkCount??0}</div>
                <div style={{fontSize:9,color:N.textM}}>Gelen link</div></div>
            </div>
            <div style={{flex:1,background:N.elevated,borderRadius:8,padding:"8px 10px",display:"flex",alignItems:"center",gap:7}}>
              <ArrowUpRight size={16} color={N.blue}/>
              <div><div style={{fontSize:16,fontWeight:700,color:N.text1,lineHeight:1}}>{p.outgoingLinkCount??0}</div>
                <div style={{fontSize:9,color:N.textM}}>Giden link</div></div>
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:5,fontSize:11}}>
            {p.score!=null&&(
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{color:N.textM}}>SEO skoru</span>
                <span style={{fontWeight:700,color:scoreColor}}>{p.score} {p.scoreLabel?`· ${p.scoreLabel}`:""}</span>
              </div>
            )}
            {p.clusterName&&(
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{color:N.textM}}>Cluster</span>
                <span style={{color:N.text2,maxWidth:170,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.clusterName}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tipler ──────────────────────────────────────────────────────────────────
type MapPage={id:number;url:string;title?:string|null;isPillar:boolean};

type HNode={
  id:string; kind:"domain"|"lang"|"cluster"|"page";
  label:string; hue:number;
  clusterId?:number; pageId?:number; isPillar?:boolean;
  pageCount?:number; hiddenCount?:number; expanded?:boolean;
  flag?:string; langCount?:number; isOrphan?:boolean;
  children:HNode[]; leaves:number; depth:number;
};

// ─── Hiyerarşi kurma ──────────────────────────────────────────────────────────
// expanded: tıklanıp açılmış cluster id'leri. Açık değilse SADECE pillar sayfalar
// gösterilir (referanstaki temiz görünüm); açıksa o cluster'ın tüm sayfaları.
// Her node bir `hue` taşır: dil ana hue'yu verir, alt-cluster'lar kardeşleri
// arasında hafifçe kaydırılır → dal/alt-cluster gözle ayırt edilir, ama dil
// ailesi korunur.
function buildHierarchy(
  tree:BoardCluster[], domainUrl:string,
  pagesMap:Map<number,MapPage[]>, expanded:Set<number>,
  collapsedLangs:Set<string>,
  visible:Set<number>|null=null, // odak modunda yalnız bu cluster'lar render edilir
  // Atanmamış sayfa bir cluster'a bağlı olmadığı için pillar da olamaz;
  // isPillar istemiyoruz (aşağıda zaten sabit false yazılıyor).
  orphanPages:Omit<MapPage,"isPillar">[]=[],
):HNode{
  // toplam cluster/sayfa sayısı (UI kapalı olsa bile gerçek toplam)
  let totC=0, totP=0;
  const countAll=(arr:BoardCluster[])=>{
    for(const c of arr){ totC++; totP+=pagesMap.get(c.id)?.length??0; if(c.children) countAll(c.children); }
  };
  countAll(tree);

  // hue: kardeşler arasında ±spread kadar dağıt, dil ailesini koru
  const mkCluster=(c:BoardCluster, hue:number, spread:number):HNode=>{
    const kids:HNode[]=[];
    const subs=(c.children??[]).filter(s=> !visible || visible.has(s.id));
    subs.forEach((sub,i)=>{
      const subHue = subs.length>1 ? hue + ((i/(subs.length-1))-0.5)*2*spread : hue;
      kids.push(mkCluster(sub,subHue,spread*0.6));
    });
    const pages=pagesMap.get(c.id)??[];
    const isOpen=expanded.has(c.id);
    const shown=isOpen ? pages : pages.filter(p=>p.isPillar);
    for(const pg of shown){
      const raw=pg.title??(pg.url.replace(/^https?:\/\/[^/]+/,"")||pg.url);
      kids.push({id:`pg-${pg.id}`,kind:"page",label:ellipsize(raw.replace(/\/$/,"")||"/",PAGE_LABEL_MAX),
        hue,pageId:pg.id,clusterId:c.id,isPillar:pg.isPillar,children:[],leaves:0,depth:0});
    }
    return {id:`c-${c.id}`,kind:"cluster",label:strip(c.name),hue,clusterId:c.id,
      pageCount:pages.length||c.pageCount||0,
      hiddenCount:Math.max(0,pages.length-shown.length),expanded:isOpen,
      children:kids,leaves:0,depth:0};
  };

  // dile göre grupla (sıra korunur)
  const langMap=new Map<string,BoardCluster[]>();
  for(const c of tree){
    const l=getLang(c.name)??"?";
    if(!langMap.has(l)) langMap.set(l,[]);
    langMap.get(l)!.push(c);
  }

  const langNodes:HNode[]=[];
  [...langMap.entries()].forEach(([lang,clusters],li)=>{
    const baseHue=langHue(lang,li);
    const isCollapsed=collapsedLangs.has(lang);
    const visClusters=clusters.filter(c=> !visible || visible.has(c.id));
    const children=isCollapsed?[]:visClusters.map((c,i)=>{
      const cHue = visClusters.length>1 ? baseHue + ((i/(visClusters.length-1))-0.5)*2*32 : baseHue;
      return mkCluster(c,cHue,18);
    });
    langNodes.push({
      id:`lang-${lang}`,kind:"lang",label:lang,hue:baseHue,
      flag:LANG_FLAG[lang]??"🌐",langCount:clusters.length,expanded:!isCollapsed,
      children,leaves:0,depth:0,
    });
  });

  // "Atanmamış" grubu (odak modunda gösterme) — sürükleyip cluster'a atanabilir
  if(!visible && orphanPages.length){
    const oHue=-25; // bandın kenarı — atanmamışlar soluk bordo
    langNodes.push({
      id:"lang-__orphan__",kind:"lang",label:"Atanmamış",hue:oHue,
      flag:"📂",langCount:orphanPages.length,expanded:true,isOrphan:true,
      children:orphanPages.map(pg=>{
        const raw=pg.title??(pg.url.replace(/^https?:\/\/[^/]+/,"")||pg.url);
        return {id:`pg-${pg.id}`,kind:"page" as const,label:ellipsize(raw.replace(/\/$/,"")||"/",PAGE_LABEL_MAX),
          hue:oHue,pageId:pg.id,isPillar:false,children:[],leaves:0,depth:0};
      }),
      leaves:0,depth:0,
    });
  }

  return {id:"domain",kind:"domain",label:domainUrl||"Site Kökü",hue:1,
    pageCount:totP,langCount:totC,children:langNodes,leaves:0,depth:0};
}

// Odak: bir cluster id'si için dilini, atalarını ve tüm alt cluster'larını bul.
function focusInfo(tree:BoardCluster[], targetId:number){
  let found:BoardCluster[]|null=null;
  const dfs=(arr:BoardCluster[], acc:BoardCluster[]):boolean=>{
    for(const c of arr){
      const next=[...acc,c];
      if(c.id===targetId){ found=next; return true; }
      if(c.children && dfs(c.children,next)) return true;
    }
    return false;
  };
  dfs(tree,[]);
  const path=found as BoardCluster[]|null;
  if(!path||!path.length) return null;
  const ancestors=new Set<number>(path.map(c=>c.id));
  const descendants=new Set<number>();
  const collect=(c:BoardCluster)=>{ for(const s of c.children??[]){ descendants.add(s.id); collect(s); } };
  collect(path[path.length-1]);
  const lang=getLang(path[0].name)??"?";
  return {lang, descendants, visible:new Set<number>([...ancestors,...descendants])};
}

// yaprak sayısı + derinlik
function measure(node:HNode, depth:number):number{
  node.depth=depth;
  if(!node.children.length){ node.leaves=1; return 1; }
  let s=0; for(const ch of node.children) s+=measure(ch,depth+1);
  node.leaves=s; return s;
}
function maxDepthOf(node:HNode):number{
  if(!node.children.length) return node.depth;
  return Math.max(...node.children.map(maxDepthOf));
}

// ─── Radyal yerleşim → ReactFlow node/edge ───────────────────────────────────
function layout(root:HNode):{nodes:Node[];edges:Edge[]}{
  measure(root,0);
  const maxDepth=Math.max(maxDepthOf(root),1);

  // DİL HALKASI HER ZAMAN EŞİT BÖLÜNÜR (3 dil → 3 eşit dilim).
  // Yoğunluk her dilin KENDİ diliminde hesaplanır: bir dil çok sayfalı olsa
  // bile öteki dilleri sıkıştırmaz, hexagonlar çakışmaz.
  const nLang=Math.max(root.children.length,1);
  const wedge=(2*Math.PI)/nLang;
  // en kalabalık dilin yaprakları kendi diliminde sığacak kadar dış yarıçap
  const maxLangLeaves=Math.max(1,...root.children.map(c=>c.leaves));
  const Rmax=Math.max(MIN_RING*maxDepth, (maxLangLeaves*LEAF_SPACING)/wedge);
  const ringStep=Rmax/maxDepth;
  const radiusAt=(d:number)=> d*ringStep;

  const nodes:Node[]=[]; const edges:Edge[]=[];

  // açısal dilim ata + node üret. polar verilirse konum oradan (yerel yerleşim).
  const place=(node:HNode, a0:number, a1:number, parentId:string|null, polar?:{r:number;a:number})=>{
    const mid=polar?polar.a:(a0+a1)/2;
    const r=polar?polar.r:radiusAt(node.depth);
    const x=r*Math.cos(mid), y=r*Math.sin(mid);
    const pillar=node.kind==="page"&&!!node.isPillar;
    const accent = node.kind==="domain" ? N.blue
                 : colorFor(node.hue, node.kind as "lang"|"cluster"|"page", node.depth, pillar);
    const bg = node.kind==="domain" ? N.surface
             : bgFor(node.hue, node.kind as "lang"|"cluster"|"page", pillar);
    const sz = node.kind==="cluster" ? clusterSize(node.depth, node.pageCount??0) : 0;

    if(node.kind==="domain"){
      nodes.push({id:node.id,type:"domain",draggable:false,zIndex:50,
        position:{x:-SZ.DOMAIN/2,y:-SZ.DOMAIN/2},
        data:{label:node.label,clusterCount:node.langCount,pageCount:node.pageCount}});
    } else if(node.kind==="lang"){
      nodes.push({id:node.id,type:"lang",draggable:false,zIndex:40,
        position:{x:x-SZ.LANG/2,y:y-SZ.LANG/2},
        data:{label:node.label,flag:node.flag,count:node.langCount,accent,bg,expanded:node.expanded,isOrphan:node.isOrphan}});
    } else if(node.kind==="cluster"){
      nodes.push({id:node.id,type:"cluster",draggable:true,zIndex:30,
        position:{x:x-sz/2,y:y-sz/2},
        data:{kind:"cluster",clusterId:node.clusterId,id:node.clusterId,
              label:node.label,pageCount:node.pageCount,accent,bg,sz,
              hiddenCount:node.hiddenCount,expanded:node.expanded}});
    } else {
      nodes.push({id:node.id,type:"page",draggable:true,zIndex:pillar?25:20,
        position:{x:x-SZ.PAGE_W/2,y:y-SZ.PAGE_H/2},
        data:{kind:"page",pageId:node.pageId,id:node.pageId,clusterId:node.clusterId,
              label:node.label,isPillar:node.isPillar,accent,bg}});
    }

    if(parentId){
      edges.push({id:`e-${parentId}-${node.id}`,source:parentId,target:node.id,
        type:"straight",
        style:{
          stroke: node.kind==="lang" ? `${accent}aa`
                 : node.kind==="cluster" ? `${accent}80`
                 : pillar ? `${accent}95` : `${accent}40`,
          strokeWidth: node.kind==="lang"?2.6 : node.kind==="cluster"?2 : pillar?1.7:1.1,
          strokeDasharray: node.kind==="page"&&!pillar ? "5 4" : undefined,
        }});
    }

    // ── Çocuk dağıtımı ────────────────────────────────────────────────────
    const subKids =node.children.filter(c=>c.kind!=="page");
    const pageKids=node.children.filter(c=>c.kind==="page");

    // alt cluster'lar → dendrogram, dil ise EŞİT dilim
    if(subKids.length){
      const subLeaves=subKids.reduce((s,c)=>s+c.leaves,0)||1;
      let cur=a0; const span=a1-a0;
      for(const ch of subKids){
        const frac = node.kind==="domain" ? 1/subKids.length : ch.leaves/subLeaves;
        place(ch,cur,cur+span*frac,node.id);
        cur+=span*frac;
      }
    }

    // sayfalar → cluster (veya "Atanmamış" hub) çevresinde YEREL çok-satırlı yay.
    // pillar cluster'a en yakın; diğerleri dışa doğru ızgara halinde yayılır.
    if(pageKids.length && (node.kind==="cluster"||node.kind==="lang")){
      const pillarPgs=pageKids.filter(p=>p.isPillar);
      const otherPgs =pageKids.filter(p=>!p.isPillar);
      const wedgeHalf=Math.min((a1-a0)/2*0.95, 0.92);   // kendi diliminde kal
      // kart yatay yarı-genişliği kadar + pay bırak ki cluster'a binmesin
      const bandStart=r + sz/2 + SZ.PAGE_W/2 + 64;
      const rowGap   =SZ.PAGE_H + 46;

      // pillar: cluster'a en yakın, mid açıda
      pillarPgs.forEach((p,i)=>{
        const a=mid + (pillarPgs.length>1 ? (i-(pillarPgs.length-1)/2)*0.11 : 0);
        place(p,a,a,node.id,{r:bandStart,a});
      });

      // diğer sayfalar: satır-sütun ızgarası (açısal kapasiteye göre sütun)
      if(otherPgs.length){
        const r0=bandStart + (pillarPgs.length?rowGap:0);
        const cols=Math.max(1, Math.floor((2*wedgeHalf*r0)/(SZ.PAGE_W*1.06)));
        otherPgs.forEach((p,idx)=>{
          const row=Math.floor(idx/cols);
          const rr =r0 + row*rowGap;
          const inRow=Math.min(cols, otherPgs.length-row*cols);
          const a = inRow>1 ? mid - wedgeHalf + ((idx%cols)/(inRow-1))*2*wedgeHalf : mid;
          place(p,a,a,node.id,{r:rr,a});
        });
      }
    }

    // PILLAR DESTEK LİNKLERİ: cluster içindeki normal sayfalar pillar sayfayı
    // linkle destekler → bunu bağlantı çizgisiyle göster (internal linking).
    if(node.kind==="cluster"){
      const pageKids=node.children.filter(c=>c.kind==="page");
      const pillarKid=pageKids.find(p=>p.isPillar);
      if(pillarKid && pageKids.length>1){
        const pColor=colorFor(node.hue,"page",node.depth,true);
        for(const pk of pageKids){
          if(pk.id===pillarKid.id) continue;
          edges.push({id:`lk-${pk.id}-${pillarKid.id}`,source:pk.id,target:pillarKid.id,
            type:"straight",zIndex:5,
            style:{stroke:`${pColor}66`,strokeWidth:1,strokeDasharray:"2 4"}});
        }
      }
    }
  };

  place(root,0,2*Math.PI,null);
  return {nodes,edges};
}

// ─── Props ───────────────────────────────────────────────────────────────────
export interface ClusterNeuralMapProps{
  tree:BoardCluster[];
  domainUrl?:string;
  pagesMap?:Map<number,MapPage[]>;
  selectedClusterId:number|null;
  onSelectCluster:(id:number|null)=>void;
  onAddCluster?:(name:string,parentId?:number|null)=>Promise<void>;
  onMovePage?:(pageId:number,targetClusterId:number|null)=>Promise<void>; // null = atanmamışa al
  onMoveCluster?:(clusterId:number,newParentId:number|null)=>Promise<void>;
  onSetPillar?:(clusterId:number,pageId:number|null)=>Promise<void>;
  /** Seçili cluster'ı sil (sayfaları "atanmamış"a düşer) */
  onDeleteCluster?:(clusterId:number)=>void;
  /** Hiçbir cluster'a atanmamış sayfalar — sürükleyip cluster'a atanabilir */
  orphanPages?:{id:number;url:string;title?:string|null}[];
  /** Header'dan kontrol edilen odak cluster (verilirse kontrollü mod) */
  focusClusterId?:number|null;
  onFocusCluster?:(id:number|null)=>void;
  /** Header aramasından seçilen sayfa → link özeti açılır */
  focusPageId?:number|null;
  /** domain id — manuel konum & açık/kapalı durum DB'de saklanır (cihazlar arası senkron) */
  persistKey?:string;
}

// ─── İç bileşen ──────────────────────────────────────────────────────────────
function Inner({
  tree,domainUrl="",pagesMap=new Map(),
  selectedClusterId,onSelectCluster,onAddCluster,
  onMovePage,onMoveCluster,onSetPillar,onDeleteCluster,orphanPages=[],
  focusClusterId:focusProp,onFocusCluster,focusPageId,persistKey,
}:ClusterNeuralMapProps){
  // ── DB kalıcılığı (manuel konum + açık/kapalı durum, cihazlar arası senkron) ──
  const layoutUrl = persistKey ? `/api/domains/${persistKey}/map-layout` : null;
  const loadedRef = useRef(false);

  // tıklanıp sayfaları açılmış cluster'lar (varsayılan: hepsi kapalı = temiz)
  const [expanded,setExpanded]=useState<Set<number>>(new Set());
  // kapalı dil grupları — tüm diller temiz başlasın diye ilk render'da doldurulur
  const [collapsedLangs,setCollapsedLangs]=useState<Set<string>|null>(null);
  // odak: kontrollü (header) ya da içsel
  const isControlled=focusProp!==undefined;
  const [internalFocus,setInternalFocus]=useState<number|null>(null);
  const focusClusterId = isControlled ? (focusProp??null) : internalFocus;
  const setFocus=useCallback((id:number|null)=>{
    if(isControlled) onFocusCluster?.(id); else setInternalFocus(id);
  },[isControlled,onFocusCluster]);
  // tıklanan sayfa → link özeti
  const [selectedPageId,setSelectedPageId]=useState<number|null>(null);

  const allLangs=useMemo(()=>{
    const s=new Set<string>();
    for(const c of tree){ const m=c.name.match(/^\[([A-Z]{2})\] /); s.add(m?.[1]??"?"); }
    return s;
  },[tree]);

  // odak aktifse: sadece o cluster'ın dili açık, görünür set = atalar+alt cluster'lar,
  // alt cluster'ların sayfaları açık.
  const focus=useMemo(()=> focusClusterId!=null ? focusInfo(tree,focusClusterId) : null,[tree,focusClusterId]);
  // Bu set'ler her render'da yeni obje olmamalı; yoksa initN useMemo sürekli
  // yeniden hesaplanır → setNodes sonsuz döngü → çökme.
  const effCollapsed = useMemo(()=> focus
    ? new Set([...allLangs].filter(l=>l!==focus.lang))
    : (collapsedLangs??allLangs)
  ,[focus,allLangs,collapsedLangs]);
  const effExpanded = useMemo(()=> focus
    ? new Set<number>([focusClusterId!, ...focus.descendants])
    : expanded
  ,[focus,focusClusterId,expanded]);
  const effVisible = focus ? focus.visible : null;

  const {nodes:initN,edges:initE}=useMemo(
    ()=>layout(buildHierarchy(tree,domainUrl,pagesMap,effExpanded,effCollapsed,effVisible,orphanPages)),
    [tree,domainUrl,pagesMap,effExpanded,effCollapsed,effVisible,orphanPages]
  );
  const [nodes,setNodes,onNodesChange]=useNodesState(initN);
  const [edges,setEdges,onEdgesChange]=useEdgesState(initE);
  const {getIntersectingNodes,fitView}=useReactFlow();
  const dropTargetRef=useRef<string|null>(null);
  // boşluğa sürüklenip serbest bırakılan node'ların kalıcı konumları
  const [manualPos,setManualPos]=useState<Map<string,{x:number;y:number}>>(new Map());

  // ── DB'den yükle (mount'ta) — cihazlar arası senkron ──
  useEffect(()=>{
    if(!layoutUrl){ loadedRef.current=true; return; }
    let cancelled=false;
    (async()=>{
      try{
        const r=await fetch(layoutUrl);
        if(r.ok){
          const data=await r.json() as {pos?:[string,{x:number;y:number}][];expanded?:number[];collapsed?:string[]|null};
          if(!cancelled&&data&&typeof data==="object"){
            if(Array.isArray(data.pos)) setManualPos(new Map(data.pos));
            if(Array.isArray(data.expanded)) setExpanded(new Set(data.expanded));
            if(data.collapsed!==undefined) setCollapsedLangs(data.collapsed===null?null:new Set(data.collapsed));
          }
        }
      }catch{ /* ağ hatası — yoksay */ }
      if(!cancelled) loadedRef.current=true;
    })();
    return ()=>{ cancelled=true; };
  },[layoutUrl]);

  // ── DB'ye kaydet (debounced) — yalnız ilk yükleme tamamlandıktan sonra ──
  useEffect(()=>{
    if(!layoutUrl||!loadedRef.current) return;
    const t=setTimeout(()=>{
      fetch(layoutUrl,{ method:"PUT", headers:{"content-type":"application/json"},
        body:JSON.stringify({
          pos:[...manualPos.entries()],
          expanded:[...expanded],
          collapsed: collapsedLangs===null ? null : [...collapsedLangs],
        }) }).catch(()=>{});
    },700);
    return ()=>clearTimeout(t);
  },[layoutUrl,manualPos,expanded,collapsedLangs]);

  // layout değişince node & edge'leri tazele — manuel konumlar korunur
  const applyLayout=useCallback((ns:Node[])=>
    manualPos.size? ns.map(n=>manualPos.has(n.id)?{...n,position:manualPos.get(n.id)!}:n) : ns
  ,[manualPos]);
  useEffect(()=>{ setNodes(applyLayout(initN)); setEdges(initE); },[initN,initE,applyLayout,setNodes,setEdges]);

  // pillar yap/kaldır
  const handleTogglePillar=useCallback((clusterId:number,pageId:number,isPillar:boolean)=>{
    onSetPillar?.(clusterId, isPillar?null:pageId);
  },[onSetPillar]);

  const displayNodes=useMemo(()=>nodes.map(n=>{
    const sel=selectedClusterId!==null&&(n.data as {id?:number}).id===selectedClusterId;
    if(n.type==="page"){
      const pd=n.data as {pageId?:number;clusterId?:number;isPillar?:boolean};
      const isSel=selectedPageId!=null && pd.pageId===selectedPageId;
      return {...n,selected:isSel,data:{...n.data,
        onPillar: (onSetPillar && pd.clusterId!=null && pd.pageId!=null)
          ? ()=>handleTogglePillar(pd.clusterId!,pd.pageId!,!!pd.isPillar) : undefined,
        // çöp kutusu: sayfayı atanmamışa al (zaten atanmamışsa gösterme)
        onTrash: (onMovePage && pd.pageId!=null && pd.clusterId!=null)
          ? ()=>onMovePage(pd.pageId!, null) : undefined}};
    }
    if(n.type==="cluster"){
      const cd=n.data as {clusterId?:number};
      return {...n,selected:sel,data:{...n.data,
        // çöp kutusu: cluster'ı sil → sayfaları atanmamışa düşer
        onTrash: (onDeleteCluster && cd.clusterId!=null) ? ()=>onDeleteCluster(cd.clusterId!) : undefined}};
    }
    return {...n,selected:sel};
  }),[nodes,selectedClusterId,selectedPageId,onSetPillar,handleTogglePillar,onMovePage,onDeleteCluster]);

  // tıklama davranışı:
  //  • dil etiketi → o dilin cluster'larını aç/kapat
  //  • cluster    → seç + sayfalarını aç/kapat
  //  • sayfa      → link özetini göster
  const onNodeClick=useCallback((_:React.MouseEvent,node:Node)=>{
    if(node.type==="page"){
      const pid=(node.data as {pageId?:number}).pageId;
      setSelectedPageId(pid??null);
      return;
    }
    setSelectedPageId(null);
    if(node.id==="lang-__orphan__") return; // atanmamış hub'ı toggle etme
    if(node.type==="lang"){
      const lang=(node.data as {label:string}).label;
      if(focusClusterId!=null){ setFocus(null); }
      setCollapsedLangs(prev=>{
        const base=prev??allLangs;
        const next=new Set(base);
        if(next.has(lang)) next.delete(lang); else next.add(lang);
        return next;
      });
      return;
    }
    if(node.type!=="cluster") return;
    const id=(node.data as {clusterId?:number}).clusterId;
    if(typeof id!=="number") return;
    onSelectCluster(id);
    if(focusClusterId!=null) return; // odak modunda tıklama sadece seçer
    setExpanded(prev=>{
      const next=new Set(prev);
      if(next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  },[onSelectCluster,allLangs,focusClusterId,setFocus]);

  const onPaneClick=useCallback(()=>{ onSelectCluster(null); setSelectedPageId(null); setFocus(null); },[onSelectCluster,setFocus]);

  // ── Sürükleme: olası hedef cluster'ı vurgula ──────────────────────────────
  const onNodeDrag=useCallback((_:MouseEvent|TouchEvent,node:Node)=>{
    if(node.type!=="page"&&node.type!=="cluster") return;
    const hits=getIntersectingNodes(node)
      .filter(n=>(n.type==="cluster"||n.type==="lang")&&n.id!==node.id);
    const target=hits[0]?.id??null;
    if(target!==dropTargetRef.current){
      dropTargetRef.current=target;
      setNodes(ns=>ns.map(n=>n.type==="cluster"
        ? {...n,data:{...n.data,dropping:n.id===target}} : n));
    }
  },[getIntersectingNodes,setNodes]);

  const onNodeDragStop=useCallback(async(_:MouseEvent|TouchEvent,node:Node)=>{
    const target=dropTargetRef.current;
    dropTargetRef.current=null;
    setNodes(ns=>ns.map(n=>n.type==="cluster"?{...n,data:{...n.data,dropping:false}}:n));

    // boşluğa bırakıldı → konumu KALICI yap (sabit kalsın)
    if(!target){
      setManualPos(prev=>new Map(prev).set(node.id,node.position));
      return;
    }

    const tNode=nodes.find(n=>n.id===target);
    const td=tNode?.data as {clusterId?:number}|undefined;
    // başka cluster'a taşındı → manuel konumu temizle, backend güncellesin
    setManualPos(prev=>{ if(!prev.has(node.id))return prev; const next=new Map(prev); next.delete(node.id); return next; });

    if(node.type==="page"){
      const pid=(node.data as {pageId?:number}).pageId;
      if(pid!=null&&td?.clusterId!=null&&onMovePage){ await onMovePage(pid,td.clusterId); }
      else setNodes(applyLayout(initN));
    } else if(node.type==="cluster"){
      const cid=(node.data as {clusterId?:number}).clusterId;
      if(cid==null){ setNodes(applyLayout(initN)); return; }
      if(tNode?.type==="lang"){ if(onMoveCluster) await onMoveCluster(cid,null); }
      else if(td?.clusterId!=null&&td.clusterId!==cid&&onMoveCluster){ await onMoveCluster(cid,td.clusterId); }
      else setNodes(applyLayout(initN));
    }
  },[nodes,initN,applyLayout,onMovePage,onMoveCluster,setNodes]);

  // ── Legend için mevcut dilleri renkleriyle topla ──────────────────────────
  const legendLangs=useMemo(()=>{
    const seen:string[]=[];
    for(const c of tree){ const m=c.name.match(/^\[([A-Z]{2})\] /); const l=m?.[1]??"?"; if(!seen.includes(l)) seen.push(l); }
    return seen.map((l,i)=>({lang:l,flag:LANG_FLAG[l]??"🌐",color:hslHex(brandHue(langHue(l,i)),74,62)}));
  },[tree]);

  // header'dan sayfa seçilince link özetini senkronla
  useEffect(()=>{ if(focusPageId!==undefined) setSelectedPageId(focusPageId); },[focusPageId]);

  // odak (header'dan) değişince cluster'ı seç + haritayı o bölgeye zoomla.
  // node'lar yerleşip ölçülmeden fitView yaparsak boş alana zoomlar (siyah ekran),
  // bu yüzden iki rAF + gecikme ile bekliyoruz.
  useEffect(()=>{
    if(focusClusterId==null) return;
    onSelectCluster(focusClusterId);
    let raf1=0,raf2=0; let t=0;
    raf1=requestAnimationFrame(()=>{ raf2=requestAnimationFrame(()=>{
      t=window.setTimeout(()=>{ try{ fitView({padding:0.2,duration:500,minZoom:0.1,maxZoom:1.4}); }catch{} },120);
    }); });
    return ()=>{ cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[focusClusterId,initN]);

  // ── Haritayı varsayılana döndür (manuel konum + açık/kapalı + odak temizle) ──
  const resetMap=useCallback(()=>{
    setManualPos(new Map());
    setExpanded(new Set());
    setCollapsedLangs(null);   // null = tüm diller kapalı = temiz başlangıç
    setFocus(null);
    setSelectedPageId(null);
    setTimeout(()=>{ try{ fitView({padding:0.15,duration:500}); }catch{} },140);
  },[setFocus,fitView]);

  // ── Toolbar / Dock / Legend ───────────────────────────────────────────────
  const [tool,setTool]=useState("pointer");
  const tools=[
    {id:"hand",icon:Hand,label:"Pan"},
    {id:"pointer",icon:MousePointer2,label:"Seç"},
    {id:"link",icon:Link2,label:"Bağla"},
    {id:"crop",icon:Scissors,label:"Kes"},
    {id:"fullscreen",icon:Maximize2,label:"Tam ekran"},
  ];
  const dock=[
    {id:"del",icon:Trash2,label:"Sil",fn:()=>{ if(selectedClusterId!=null) onDeleteCluster?.(selectedClusterId); }},
    {id:"sub",icon:Layers,label:"Alt Cluster",fn:()=>{ if(selectedClusterId!=null) onAddCluster?.("Alt Cluster",selectedClusterId); }},
    {id:"reset",icon:RotateCcw,label:"Haritayı Sıfırla",fn:resetMap},
  ];
  const glass={background:"rgba(255,255,255,.92)",backdropFilter:"blur(14px)",
    border:"1px solid #e7e7e4",boxShadow:"0px 12px 16px -4px rgba(20,20,18,.08),0px 4px 6px -2px rgba(20,20,18,.03)"} as const;

  return (
    <div style={{width:"100%",height:"100%",position:"relative",
      background:"radial-gradient(1200px 700px at 50% -10%,#ffffff 0%,#f4f7ff 38%,#eef1f8 100%)"}}>
      {/* 3D sahne derinliği: üstten ışık + kenarlarda vinyet */}
      <div style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:0,
        background:"radial-gradient(900px 520px at 50% 0%,rgba(232,62,60,.10),transparent 60%),radial-gradient(140% 100% at 50% 50%,transparent 55%,rgba(20,20,18,.10) 100%)"}}/>
      {/* Odak modu rozeti — üst orta */}
      {focusClusterId!=null&&(
        <div style={{...glass,position:"absolute",top:14,left:"50%",transform:"translateX(-50%)",zIndex:30,
          display:"flex",alignItems:"center",gap:8,borderRadius:20,padding:"6px 8px 6px 14px"}}>
          <span style={{fontSize:12,color:N.text2}}>🎯 Odak modu — yalnız seçili cluster zinciri</span>
          <button onClick={()=>setFocus(null)} title="Odağı kapat"
            style={{border:"none",background:N.elevated,cursor:"pointer",color:N.text2,
              borderRadius:14,padding:"4px 8px",display:"flex",alignItems:"center",gap:4,fontSize:11}}>
            <X size={13}/> Kapat
          </button>
        </div>
      )}

      {/* Sayfa link özeti — sağ üst */}
      {selectedPageId!=null&&(
        <PageLinkSummary pageId={selectedPageId} onClose={()=>setSelectedPageId(null)} glass={glass}/>
      )}

      {/* Toolbar — sol orta */}
      <div style={{...glass,position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",zIndex:20,
        display:"flex",flexDirection:"column",gap:4,borderRadius:12,padding:6}}>
        {tools.map(({id,icon:Icon,label})=>(
          <button key={id} onClick={()=>setTool(id)} title={label} style={{padding:8,borderRadius:8,border:"none",
            cursor:"pointer",background:tool===id?`${N.purple}22`:"transparent",color:tool===id?N.purple:N.textM}}>
            <Icon size={16}/>
          </button>
        ))}
      </div>

      {/* Legend — sol alt */}
      <div style={{...glass,position:"absolute",bottom:70,left:72,zIndex:20,borderRadius:12,padding:"10px 14px",minWidth:190,maxWidth:230}}>
        <p style={{fontSize:9,fontWeight:600,textTransform:"uppercase",letterSpacing:".08em",color:N.textM,margin:"0 0 6px"}}>DİLLER (RENK AİLESİ)</p>
        <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:8}}>
          {legendLangs.map(({lang,flag,color})=>(
            <div key={lang} style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:12,height:12,clipPath:HEX,flexShrink:0,background:`${color}40`,border:`1.5px solid ${color}`}}/>
              <span style={{fontSize:10,color:N.text2}}>{flag} {lang}</span>
            </div>
          ))}
        </div>
        <div style={{borderTop:`1px solid ${N.borderS}`,paddingTop:7}}>
          <p style={{fontSize:9,fontWeight:600,textTransform:"uppercase",letterSpacing:".08em",color:N.textM,margin:"0 0 5px"}}>OKUMA</p>
          {[
            {sw:(c:string)=><div style={{width:11,height:11,borderRadius:"50%",background:`${c}30`,border:`1.5px solid ${c}`}}/>,t:"Ton koyu = ana cluster"},
            {sw:(c:string)=><div style={{width:11,height:11,borderRadius:"50%",background:`${c}30`,border:`1.5px solid ${c}`}}/>,t:"Ton açık = alt cluster"},
            {sw:(_c:string)=><div style={{width:18,height:9,borderRadius:2,background:"#12b76a44",border:"1px solid #12b76a"}}/>,t:"Açık dolgu = sayfa"},
            {sw:(_c:string)=><span style={{fontSize:11}}>★</span>,t:"★ rozet = Pillar Page"},
            {sw:(c:string)=><div style={{width:18,height:0,borderTop:`1.5px dashed ${c}`}}/>,t:"Noktalı çizgi = pillar'a link"},
          ].map(({sw,t},i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
              <span style={{width:18,display:"flex",justifyContent:"center",flexShrink:0}}>{sw(legendLangs[0]?.color??N.cyan)}</span>
              <span style={{fontSize:10,color:N.text2}}>{t}</span>
            </div>
          ))}
        </div>
        <p style={{fontSize:9,color:N.textM,marginTop:7,paddingTop:7,borderTop:`1px solid ${N.borderS}`,lineHeight:1.5}}>
          💡 Dil/cluster'a tıkla → aç-kapat<br/>
          💡 Sürükle → başka cluster'a taşı
        </p>
      </div>

      {/* ActionDock — alt orta */}
      <div style={{...glass,position:"absolute",bottom:16,left:"50%",transform:"translateX(-50%)",zIndex:20,
        display:"flex",alignItems:"center",gap:2,borderRadius:20,padding:"6px 8px",whiteSpace:"nowrap"}}>
        {dock.map(({id,icon:Icon,label,fn})=>(
          <button key={id} onClick={fn} style={{display:"flex",alignItems:"center",gap:6,borderRadius:12,padding:"7px 12px",
            border:"none",background:"transparent",fontSize:12,fontWeight:500,color:N.text2,cursor:"pointer"}}
            onMouseEnter={e=>{const t=e.currentTarget;t.style.background=N.elevated;t.style.color=N.text1;}}
            onMouseLeave={e=>{const t=e.currentTarget;t.style.background="transparent";t.style.color=N.text2;}}>
            <Icon size={14}/>{label}
          </button>
        ))}
      </div>

      <ReactFlow
        nodes={displayNodes} edges={edges}
        onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick} onPaneClick={onPaneClick}
        onNodeDrag={onNodeDrag} onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        fitView fitViewOptions={{padding:0.12}}
        minZoom={0.02} maxZoom={2.5}
        proOptions={{hideAttribution:true}}
        defaultEdgeOptions={{animated:false,type:"straight"}}
        elementsSelectable nodesConnectable={false}
      >
        <Background variant={BackgroundVariant.Dots} gap={26} size={1.2} color="#d5d5d1"/>
        <MiniMap
          nodeColor={n=>{ if(n.type==="domain")return N.blue;
            const a=(n.data as {accent?:string}).accent??N.purple;
            return n.type==="page"?a+"99":a; }}
          maskColor="rgba(249,250,251,.82)"
          style={{background:N.surface,border:`1px solid ${N.border}`,borderRadius:8}}
          pannable zoomable
        />
      </ReactFlow>
    </div>
  );
}

export function ClusterNeuralMap(props:ClusterNeuralMapProps){
  return <ReactFlowProvider><Inner {...props}/></ReactFlowProvider>;
}
